import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import CardGrid from '../components/CardGrid';
import Modal from '../components/Modal';
import Seo from '../components/Seo';
import api from '../api/client';
import './UserProfile.css';

/** Scan our followers (paginated) to find out whether `publicId` follows us. */
async function checkFollowsMe(publicId) {
    let skip = 0;
    const limit = 25;
    for (let i = 0; i < 12; i++) {
        const res = await api.getFollowers(skip, limit);
        const items = res?.items || [];
        if (items.some((u) => u.publicId === publicId)) return true;
        skip += items.length;
        if (items.length === 0 || skip >= (res?.total || 0)) break;
    }
    return false;
}

export default function UserProfile({ translations }) {
    const { publicId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [profile, setProfile] = useState(null);
    const [pictures, setPictures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [isFollowing, setIsFollowing] = useState(false);
    const [followsMe, setFollowsMe] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [messageBusy, setMessageBusy] = useState(false);

    const [followersInfo, setFollowersInfo] = useState({ count: null, private: false });
    const [followingInfo, setFollowingInfo] = useState({ count: null, private: false });
    const [listModal, setListModal] = useState(null); // 'followers' | 'following'
    const [listItems, setListItems] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listPrivate, setListPrivate] = useState(false);

    const t = (key, fallback) => translations?.[key] || fallback;

    const isSelf = user && profile && user.publicId === profile.publicId;
    const isMutual = isFollowing && followsMe;

    const load = useCallback(async () => {
        setLoading(true);
        setNotFound(false);
        try {
            const data = await api.findUser(publicId);
            setProfile(data);
            setIsFollowing(Boolean(data.isFollowing));

            const [pics, follows, followersRes, followingRes] = await Promise.all([
                api.getUserPictures(publicId, 0, 50).catch(() => []),
                user ? checkFollowsMe(publicId).catch(() => false) : Promise.resolve(false),
                api.getUserFollowers(publicId, 0, 1).then((r) => ({ count: r.total })).catch(() => ({ private: true })),
                api.getUserFollowing(publicId, 0, 1).then((r) => ({ count: r.total })).catch(() => ({ private: true })),
            ]);
            setPictures(Array.isArray(pics) ? pics : []);
            setFollowsMe(follows);
            setFollowersInfo({ count: followersRes.count ?? null, private: !!followersRes.private });
            setFollowingInfo({ count: followingRes.count ?? null, private: !!followingRes.private });
        } catch {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    }, [publicId, user]);

    const openList = async (type) => {
        setListModal(type);
        setListItems([]);
        setListPrivate(false);
        setListLoading(true);
        try {
            const res = type === 'followers'
                ? await api.getUserFollowers(publicId, 0, 25)
                : await api.getUserFollowing(publicId, 0, 25);
            setListItems(res?.items || []);
        } catch {
            setListPrivate(true);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => { load(); }, [load]);

    const handleFollowToggle = async () => {
        if (!user) { navigate('/login'); return; }
        if (followBusy) return;
        setFollowBusy(true);
        try {
            if (isFollowing) {
                await api.unsubscribe(profile.publicId);
                setIsFollowing(false);
            } else {
                const res = await api.subscribe(profile.publicId);
                setIsFollowing(true);
                // subscribe tells us whether the relationship became mutual
                if (res && typeof res.isMutual === 'boolean') {
                    setFollowsMe(res.isMutual);
                }
            }
        } catch {
            // Ignore — state stays as it was
        } finally {
            setFollowBusy(false);
        }
    };

    const handleSendMessage = async () => {
        if (!user) { navigate('/login'); return; }
        if (!isMutual || messageBusy) return;
        setMessageBusy(true);
        try {
            const discussion = await api.createDiscussion([profile.publicId]);
            navigate(`/messages/${discussion.id}`);
        } catch {
            setMessageBusy(false);
        }
    };

    const renderMessageButton = () => {
        if (isSelf) return null;
        if (isMutual) {
            return (
                <button className="up-btn up-btn-secondary" onClick={handleSendMessage} disabled={messageBusy}>
                    {messageBusy ? t('user-opening', 'Opening...') : t('user-send-message', 'Send message')}
                </button>
            );
        }
        const reason = !followsMe
            ? t('user-msg-not-following-you', "This person doesn't follow you")
            : t('user-msg-follow-them', 'Follow each other to start a conversation');
        return (
            <div className="up-message-disabled-wrap">
                <button className="up-btn up-btn-secondary" disabled title={reason}>
                    {t('user-send-message', 'Send message')}
                </button>
                <span className="up-message-hint">{reason}</span>
            </div>
        );
    };

    let body;
    if (loading) {
        body = <div className="spinner" />;
    } else if (notFound || !profile) {
        body = (
            <div className="up-missing">
                <p>{t('user-not-found', 'This user is not available.')}</p>
            </div>
        );
    } else {
        body = (
            <>
                <div className="up-header">
                    <div className="up-banner">
                        {profile.bannerPicture && (
                            <img src={profile.bannerPicture} alt="" className="up-banner-img" />
                        )}
                    </div>
                    <div className="up-header-main">
                        {profile.profilePicture ? (
                            <img src={profile.profilePicture} alt="" className="up-avatar" />
                        ) : (
                            <div className="up-avatar up-avatar-fallback">
                                {(profile.username || '?')[0].toUpperCase()}
                            </div>
                        )}
                        <div className="up-identity">
                            <div className="up-name-row">
                                <h1 className="up-username">{profile.username || 'Unknown'}</h1>
                                {profile.userType === 'artist' && (
                                    <span className="up-badge verified" title={t('user-verified-artist', 'Verified artist')}>
                                        ✓ {t('user-artist', 'Artist')}
                                    </span>
                                )}
                                {followsMe && !isSelf && (
                                    <span className="up-badge">
                                        {isMutual ? t('user-mutual', 'Mutual') : t('user-follows-you', 'Follows you')}
                                    </span>
                                )}
                            </div>
                            {(profile.city || profile.age != null) && (
                                <p className="up-meta">
                                    {profile.city}
                                    {profile.city && profile.age != null ? ' · ' : ''}
                                    {profile.age != null ? `${profile.age} ${t('user-years', 'yrs')}` : ''}
                                </p>
                            )}
                            {profile.description && <p className="up-description">{profile.description}</p>}
                            <div className="up-follow-stats">
                                <button className="up-follow-stat" onClick={() => openList('followers')}>
                                    <strong>{followersInfo.private ? '🔒' : (followersInfo.count ?? 0)}</strong>
                                    <span>{t('profile-followers', 'Followers')}</span>
                                </button>
                                <button className="up-follow-stat" onClick={() => openList('following')}>
                                    <strong>{followingInfo.private ? '🔒' : (followingInfo.count ?? 0)}</strong>
                                    <span>{t('profile-following', 'Following')}</span>
                                </button>
                            </div>
                        </div>
                        {user && !isSelf && (
                            <div className="up-actions">
                                <button
                                    className={`up-btn ${isFollowing ? 'up-btn-secondary' : 'up-btn-primary'}`}
                                    onClick={handleFollowToggle}
                                    disabled={followBusy}
                                >
                                    {isFollowing ? t('user-unfollow', 'Unfollow') : t('user-follow', 'Follow')}
                                </button>
                                {renderMessageButton()}
                            </div>
                        )}
                    </div>
                </div>

                <div className="up-pictures">
                    <h2 className="up-pictures-title">{t('user-artworks', 'Artworks')}</h2>
                    <CardGrid pictures={pictures} />
                </div>
            </>
        );
    }

    return (
        <>
            {profile && !notFound && (
                <Seo
                    title={profile.username || 'Artist'}
                    description={
                        profile.description
                            ? profile.description.slice(0, 160)
                            : `Discover artworks by ${profile.username || 'this artist'} on ArtSyde.`
                    }
                    image={profile.profilePicture || profile.bannerPicture}
                    type="profile"
                    jsonLd={{
                        '@context': 'https://schema.org',
                        '@type': 'ProfilePage',
                        mainEntity: {
                            '@type': 'Person',
                            name: profile.username || 'Artist',
                            image: profile.profilePicture || undefined,
                            description: profile.description || undefined,
                        },
                    }}
                />
            )}
            <Header translations={translations} />
            <div className="up-container">{body}</div>
            {listModal && (
                <Modal
                    title={listModal === 'followers'
                        ? t('profile-followers', 'Followers')
                        : t('profile-following', 'Following')}
                    onClose={() => setListModal(null)}
                >
                    {listLoading ? (
                        <div className="loading-page"><div className="spinner" /></div>
                    ) : listPrivate ? (
                        <p className="up-list-private">🔒 {t('user-list-private', 'This list is private.')}</p>
                    ) : listItems.length === 0 ? (
                        <p className="up-list-private">{t('profile-list-empty', 'Nobody here yet.')}</p>
                    ) : (
                        <ul className="up-user-list">
                            {listItems.map((u) => (
                                <li key={u.publicId}>
                                    <Link
                                        to={`/user/${u.publicId}`}
                                        className="up-user-list-item"
                                        onClick={() => setListModal(null)}
                                    >
                                        {u.profilePicture ? (
                                            <img src={u.profilePicture} alt="" className="up-user-avatar" />
                                        ) : (
                                            <span className="up-user-avatar fallback">
                                                {(u.username || '?')[0].toUpperCase()}
                                            </span>
                                        )}
                                        <span className="up-user-name">{u.username || 'Unknown'}</span>
                                        {u.isFollowing && <span className="up-user-tag">{t('profile-following-tag', 'Following')}</span>}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </Modal>
            )}
        </>
    );
}
